import clsx from 'clsx';
import { Card } from '../../../components/Card/Card';
import { TAS_BACK_END_API_URL, URL_TAS_VERSION } from '../../../constants';
import { fetchAuthenticated } from '../../../controllers';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import './PatientInfusion.scss';
import { TextInput } from '../../../components/TextInput/TextInput';
import { Button } from '../../../components/Button/Button';
import { Pencil } from '../../../shared/assets/pencil';
import { Clock } from '../../../shared/assets/clock';
import { Header } from '../../../components/common/table/Header/Header';
import { HeaderCell } from '../../../components/common/table/HeaderCell/HeaderCell';
import { FilterButton } from '../../../components/common/buttons/iconButtons/FilterButton';
import { InfusionHistoryRow } from './InfusionHistoryRow/InfusionHistoryRow';
import { PatientRecord, Section } from '../../../shared/types/types';
import { format } from 'date-fns';
import { neutral400, neutral50, primaryBlue400, warning } from '../../../shared/styles/variables';
import { LoadingSpinner, mediumSpinner } from '../../../components/common/loader/LoadingSpinner';
import { PopupMessageContext } from 'src/shared/contexts/PopupMessageContext';
import { v4 } from 'uuid';
import { ExternalUserVerifyContext } from 'src/shared/contexts/ExternalAprrovalContext';
import { BannerMessageContext } from 'src/shared/contexts/BannerMessageContext';
import { convertDate, convertTime } from 'src/components/common/convertDateTime/convertDateTime';
import { SingleDatePicker, toMomentObject } from 'react-dates';
import TimeDropdown from 'src/components/common/timeSelector/timeSelector';
import moment, { Moment } from 'moment';

interface PatientInfusionProps {
  patientData?: PatientRecord;
  section: Section;
  token: string | null;
}

export interface InfusionSession {
  _id: string;
  body: {
    beg_date_time_utc: string;
    end_date_time_utc: string;
    status: string;
  };
  header: {
    item_id: string;
    patient_id: string;
    timestamp_local: string;
    timestamp_utc: string;
    version: number;
    user: {
      id: string;
      name: string;
    };
  };
}

export interface InfusionSessionsServiceResult {
  status: string;
  total: number;
  items: Array<InfusionSession>;
  errors: string[];
}

export interface InfusionSessionsAjaxResult {
  result: InfusionSessionsServiceResult;
}

export const PatientInfusion = ({ patientData, token }: PatientInfusionProps) => {
  const { checkAuthorization } = useContext(ExternalUserVerifyContext);
  const [infusionSessions, setInfusionSessions] = useState<InfusionSession[] | null>(null);
  const [isInfusionHistoryLoading, setInfusionHistoryIsLoading] = useState<boolean>(true);
  const [apiData, setApiData] = useState<InfusionSessionsAjaxResult>();
  const [serverError, setServerError] = useState();
  const [currentSessionId, setCurrentSessionId] = useState<string>();
  const [currentVersion, setCurrentVersion] = useState<number>(1);

  const [currentTime, setCurrentTime] = useState(new Date());

  const [isInfusionStarted, setIsInfusionStarted] = useState(false);
  const [isLoadingStartInfusion, setIsLoadingStartInfusion] = useState<boolean>(false);
  const [isLoadingEndInfusion, setIsLoadingEndInfusion] = useState<boolean>(false);

  const [openStartDate, setOpenStartDate] = useState<boolean>(false);
  const [openStartTime, setOpenStartTime] = useState<boolean>(false);

  const [openEndDate, setOpenEndDate] = useState<boolean>(false);
  const [openEndTime, setOpenEndTime] = useState<boolean>(false);

  const [tempStartDate, setTempStartDate] = useState<string>(new Date().toISOString());
  const [startDateTimeEdited, setStartDateTimeEdited] = useState<boolean>(false);
  const [tempStartTime, setTempStartTime] = useState<string>('');
  const [endDateTimeEdited, setEndDateTimeEdited] = useState<boolean>(false);

  const [tempEndDate, setTempEndDate] = useState<string>(new Date().toISOString());
  const [tempEndTime, setTempEndTime] = useState<string>('');

  const [startInput, setStartInput] = useState<string>('');
  const [endInput, setEndInput] = useState<string>('');
  const [editEndTime, setEditEndTime] = useState<boolean>(false);
  const [startInfusionEditing, setStartInfusionEditing] = useState<boolean>(false);
  const [validStartTime, setValidStartTime] = useState<boolean>(true);
  const [validEndTime, setValidEndTime] = useState<boolean>(true);

  const CURRENT_PATIENT_INFUSION = 'currentPatientInfusion';

  const { addPopupMessage } = useContext(PopupMessageContext);
  const { addBannerMessage } = useContext(BannerMessageContext);

  const getPatientInfusionData = useCallback(async () => {
    const config: RequestInit = {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
    };

    if (!patientData) return;
    setInfusionHistoryIsLoading(true);
    const url = TAS_BACK_END_API_URL + `${URL_TAS_VERSION}/patients/${patientData?._id}/infusions`;

    const response = await fetchAuthenticated<InfusionSessionsAjaxResult>(url, config);
    setInfusionHistoryIsLoading(false);

    if (response?.ok) {
      setApiData(response.parsedBody);

      let activeInfusion = response.parsedBody?.result.items.find((obj) => obj.body.status === 'INFUSION_STATUS_ACTIVE') || null;

      if (activeInfusion) {
        setStartInput(activeInfusion?.body.beg_date_time_utc);
        setCurrentSessionId(activeInfusion?.header?.item_id);
        setCurrentVersion(activeInfusion?.header?.version);
        setIsInfusionStarted(true);
        setTempEndTime(convertTime(new Date().toISOString()));
        setStartDateTimeEdited(false);
      }
    }
  }, [patientData, token]);

  useEffect(() => {
    if (!patientData) return;
    try {
      getPatientInfusionData();
    } catch (error: any) {
      setServerError(error);
    }
  }, [getPatientInfusionData, patientData]);

  const [expanded, setExpanded] = useState(true);

  const handleOnExpand = () => {
    setExpanded(!expanded);
  };

  const ascendingDateSort = (infusionA: InfusionSession, infusionB: InfusionSession) => {
    return Date.parse(infusionB.body.beg_date_time_utc) - Date.parse(infusionA.body.beg_date_time_utc);
  };

  useEffect(() => {
    async function decryptData() {
      if (apiData?.result?.items) {
        try {
          setInfusionSessions(apiData?.result.items.sort(ascendingDateSort));
        } catch {
          setInfusionSessions(null);
        }
      }
    }
    decryptData();
  }, [apiData, token]);

  const showAdd = useMemo(() => {
    if (tempStartDate !== '' && tempStartTime !== '' && endInput !== '') return true;
    else return false;
  }, [tempStartTime, tempStartDate, endInput]);

  useEffect(() => {
    if (tempStartTime === '') setTempStartTime(convertTime(new Date().toISOString()));
  }, [startInfusionEditing, tempStartTime]);

  const updateTime = () => {
    const now = new Date();
    setCurrentTime(now);
  };

  useEffect(() => {
    const timeInterval = setInterval(updateTime, 1000);
    return () => {
      clearInterval(timeInterval);
    };
  }, []);

  interface InfusionPutResponse {
    result: {
      session: InfusionSession;
    };
  }

  const handleStartInfusionClick = async () => {
    setIsLoadingStartInfusion(true);
    toggleStartDropDowns('');

    let nonEditedStartTime = null;
    if (!startDateTimeEdited) nonEditedStartTime = new Date().toISOString();

    if (!checkAuthorization) {
      setIsLoadingStartInfusion(false);
      return;
    }

    let result;
    try {
      result = await checkAuthorization();
    } catch (error) {
      setIsLoadingStartInfusion(false);
      addBannerMessage({
        id: v4(),
        type: 'error',
        message: 'Incorrect Access Credentials',
      });
      return;
    }

    if (!nonEditedStartTime) {
      let inputtedDate = moment(tempStartDate);
      const combinedDateTime = inputtedDate
        .clone()
        .hour(Number(tempStartTime.split(':')[0]))
        .minute(Number(tempStartTime.split(':')[1]))
        .second(Number(tempStartTime.split(':')[2]))
        .millisecond(0);
      nonEditedStartTime = combinedDateTime.toISOString();
    }

    if (result?.verified) {
      const body = {
        beg_date_time_utc: nonEditedStartTime,
        status: 'INFUSION_STATUS_ACTIVE',
      };
      const config: RequestInit = {
        method: 'PUT',
        body: JSON.stringify(body),
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };

      try {
        if (!patientData) return;
        const url = TAS_BACK_END_API_URL + `${URL_TAS_VERSION}/patients/${patientData?._id}/infusions`;

        const response = await fetchAuthenticated<InfusionPutResponse>(url, config);

        setIsLoadingStartInfusion(false);
        if (response?.ok) {
          const data = response?.parsedBody;
          if (!data) return;
          setStartInput(data.result.session.body.beg_date_time_utc);
          setCurrentSessionId(data?.result?.session?.header?.item_id);
          setCurrentVersion(data?.result?.session?.header?.version);
          setIsInfusionStarted(true);
          setTempEndTime(convertTime(new Date().toISOString()));
          setStartDateTimeEdited(false);

          if (infusionSessions) {
            const newInfusionSessions: InfusionSession[] = [...infusionSessions, data.result.session].sort(ascendingDateSort);
            setInfusionSessions(newInfusionSessions);
          }

          const savedSessionData = {
            startInfusionTime: new Date().toISOString(),
            infusionSessionId: data?.result?.session?.header?.item_id,
            infusionVersion: data.result?.session?.header?.version,
          };

          window.localStorage.setItem(CURRENT_PATIENT_INFUSION, JSON.stringify(savedSessionData));
        }
      } catch (error) {
        setIsLoadingStartInfusion(false);
        addPopupMessage({
          id: v4(),
          message: 'There was an error in starting the Infusion Session. Please retry request.',
          header: 'Addition',
        });
      }
    } else {
      setIsLoadingStartInfusion(false);
      addBannerMessage({
        id: v4(),
        type: 'error',
        message: 'Incorrect Access Credentials',
      });
    }
  };

  const handleEndInfusionClick = async (editedStartTime?: string, editedEndTime?: string, sessionId?: string, sessionVersion?: number) => {
    if (!sessionId) setIsLoadingEndInfusion(true);
    toggleEndDropDowns('');
    let notEditedEndTime = null;
    if (!endDateTimeEdited) notEditedEndTime = new Date().toISOString();

    if (!checkAuthorization) {
      setIsLoadingEndInfusion(false);
      return;
    }

    let result;
    try {
      result = await checkAuthorization();
    } catch (error) {
      setIsLoadingEndInfusion(false);
      addBannerMessage({
        id: v4(),
        type: 'error',
        message: 'Incorrect Access Credentials',
      });
    }
    let status = editedEndTime || editEndTime || !editedStartTime ? 'INFUSION_STATUS_COMPLETED' : 'INFUSION_STATUS_ACTIVE';

    if (!notEditedEndTime) {
      let inputtedDate = moment(tempEndDate);
      const combinedDateTime = inputtedDate
        .clone()
        .hour(Number(tempEndTime.split(':')[0]))
        .minute(Number(tempEndTime.split(':')[1]))
        .second(Number(tempEndTime.split(':')[2]))
        .millisecond(0);
      notEditedEndTime = combinedDateTime.toISOString();
    }

    if (result?.verified) {
      const now = new Date();

      let endTime: string;

      if (editedEndTime === '') {
        endTime = '';
      } else if (editedEndTime) {
        endTime = editedEndTime;
      } else {
        endTime = notEditedEndTime;
      }

      const body = {
        version: sessionVersion || currentVersion,
        beg_date_time_utc: editedStartTime || startInput,
        end_date_time_utc: endTime,
        status: status,
      };

      const config: RequestInit = {
        method: 'PATCH',
        body: JSON.stringify(body),
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };

      const sessionIdentifier = sessionId || currentSessionId;
      try {
        if (!patientData) return;
        const url = TAS_BACK_END_API_URL + `${URL_TAS_VERSION}/patients/${patientData?._id}/infusions`;
        const response = await fetchAuthenticated<InfusionPutResponse>(`${url}/${sessionIdentifier}`, config);

        setIsLoadingEndInfusion(false);
        if (response?.ok) {
          const data = response?.parsedBody?.result;

          setEndInput(format(now, 'HH:mm:ss'));
          setIsInfusionStarted(false);

          if (data?.session && infusionSessions) {
            setInfusionSessions(
              infusionSessions.map((session) => {
                if (session?.header.item_id === data?.session?.header.item_id) {
                  return data?.session;
                } else {
                  return session;
                }
              })
            );
          }
          let setNewTime = new Date().toISOString();
          setEndDateTimeEdited(false);
          setStartInput('');
          setEndInput('');
          setTempStartDate(setNewTime);
          setTempStartTime('');
          setTempEndDate(setNewTime);
          setTempEndTime('');
          setStartInfusionEditing(false);
          setEditEndTime(false);
          setOpenEndTime(false);
          setOpenEndDate(false);
          setOpenStartTime(false);
          setOpenStartDate(false);
          setIsLoadingEndInfusion(false);
          setIsLoadingStartInfusion(false);
          setIsInfusionStarted(false);

          window.localStorage.removeItem(CURRENT_PATIENT_INFUSION);
        }
      } catch (error) {
        setIsLoadingEndInfusion(false);
        addPopupMessage({
          id: v4(),
          message: 'There was an error in ending the Infusion Session. Please retry request.',
          header: 'Change',
        });
      }
    } else {
      setIsLoadingEndInfusion(false);
      addBannerMessage({
        id: v4(),
        type: 'error',
        message: 'Incorrect Access Credentials',
      });
    }
  };

  // Edit session end time

  const handleEditEndInfusionTimeClick = () => {
    setEditEndTime(!editEndTime);
    setEndInput(new Date().toISOString());
  };

  const toggleStartDropDowns = (dropDown: string) => {
    if (dropDown === 'date') {
      setOpenStartDate(!openStartDate);
      setOpenStartTime(false);
    } else if (dropDown === 'time') {
      setOpenStartDate(false);
      setOpenStartTime(!openStartTime);
    } else {
      setOpenStartDate(false);
      setOpenStartTime(false);
    }
  };

  const toggleEndDropDowns = (dropDown: string) => {
    if (dropDown === 'date') {
      setOpenEndDate(!openEndDate);
      setOpenEndTime(false);
    } else if (dropDown === 'time') {
      setOpenEndDate(false);
      setOpenEndTime(!openEndTime);
    } else {
      setOpenEndDate(false);
      setOpenEndTime(false);
    }
  };

  const displayStartDate = () => {
    let date;
    date = tempStartDate ? format(new Date(tempStartDate), 'dd-MMM-yyyy') : format(new Date(new Date()), 'dd-MMM-yyyy');
    return toMomentObject(date, 'dd-MMM-yyyy');
  };

  const displayEndDate = () => {
    let date;
    date = tempEndDate ? format(new Date(tempEndDate), 'dd-MMM-yyyy') : format(new Date(new Date()), 'dd-MMM-yyyy');
    return toMomentObject(date, 'dd-MMM-yyyy');
  };

  useEffect(() => {
    const futureTimeChecker = (startEnd: 'start' | 'end') => {
      if (!tempStartDate) return false;

      let time = startEnd === 'start' ? tempStartTime : tempEndTime;
      let inpputedDate = moment(tempStartDate, moment.ISO_8601);

      if (!inpputedDate.isValid()) return false;

      const combinedDateTime = inpputedDate
        .clone()
        .hour(Number(time.split(':')[0]))
        .minute(Number(time.split(':')[1]))
        .second(Number(time.split(':')[2]))
        .millisecond(0);

      const formattedInput = combinedDateTime.toISOString();

      const unixTimeToCheck = new Date(formattedInput).getTime();

      const currentUnixTime = new Date().getTime();

      return unixTimeToCheck < currentUnixTime ? true : false;
    };

    if (
      tempStartTime &&
      tempEndTime &&
      tempStartTime.length === 8 &&
      tempEndTime.length === 8 &&
      tempStartTime.split(':').length === 3 &&
      tempEndTime.split(':').length === 3
    ) {
      const [hours, minutes, seconds] = tempEndTime.split(':');
      if (Number(hours) > 23 || Number(minutes) > 59 || Number(seconds) > 59) return setValidEndTime(false);
      if (Number(hours) > 23 || Number(minutes) > 59 || Number(seconds) > 59) return setValidStartTime(false);
      const [startHours, startMinutes, startSeconds] = tempStartTime.split(':');
      if (startHours > hours) return setValidStartTime(false);
      if (startHours === hours && startMinutes > minutes) return setValidStartTime(false);
      if (startHours === hours && startMinutes === minutes && startSeconds > seconds) return setValidStartTime(false);

      if (!futureTimeChecker('start')) setValidStartTime(false);
      if (!futureTimeChecker('end')) setValidEndTime(false);
    }
    if ((tempStartTime && tempStartTime.length !== 8) || tempStartTime.split(':').length !== 3) {
      return setValidStartTime(false);
    } else if (tempStartTime) {
      if (tempStartTime.split(':').length !== 3) return setValidStartTime(false);
      let [hours, minutes, seconds] = tempStartTime.split(':');
      Number(hours) > 23 || Number(minutes) > 59 || Number(seconds) > 59 ? setValidStartTime(false) : setValidStartTime(true);
      if (!futureTimeChecker('start')) setValidStartTime(false);
    } else {
      setValidStartTime(false);
    }

    if (tempEndTime === '') {
      return setValidEndTime(true);
    } else if (tempEndTime && (tempEndTime.length !== 8 || tempEndTime.split(':').length !== 3)) {
      return setValidEndTime(false);
    } else if (tempEndTime && tempEndTime.length === 8 && tempEndTime.split(':').length === 3) {
      if (!futureTimeChecker('end')) setValidEndTime(false);
      const [hours, minutes, seconds] = tempEndTime.split(':');
      if (Number(hours) > 23 || Number(minutes) > 59 || Number(seconds) > 59) return setValidEndTime(false);
    }
    setValidEndTime(true);
  }, [tempStartTime, tempEndTime, tempStartDate]);

  const body = expanded ? (
    <div className="patient-infusion-content-container">
      <div className="infusion-tracker-container">
        <div className="infusion-time-container">
          <div className="clock">{format(currentTime, 'HH:mm:ss')}</div>
        </div>
        <div className="infusion-controls-container">
          <div className="start-container">
            <div className="start-infusion-spacing">
              {!startInfusionEditing || isInfusionStarted ? (
                <>
                  <TextInput value={'Manually Log Date'} onChange={() => null} width={162} disabled={true} />
                  <TextInput value={'Log Start Time'} onChange={() => null} width={162} disabled={true} />
                </>
              ) : (
                <div className="start-infusion-spacing-editing">
                  <SingleDatePicker
                    placeholder={convertDate(tempStartDate)}
                    date={displayStartDate()}
                    onDateChange={(inpputedDate: Moment | null) => {
                      if (inpputedDate) {
                        const combinedDateTime = inpputedDate
                          .clone()
                          .hour(Number(tempStartTime.split(':')[0]))
                          .minute(Number(tempStartTime.split(':')[1]))
                          .second(Number(tempStartTime.split(':')[2]))
                          .millisecond(0);
                        const formattedInput = combinedDateTime.toISOString();
                        setTempStartDate(formattedInput);
                        setStartDateTimeEdited(true);
                      }
                      setOpenStartDate(!openStartDate);
                    }}
                    appendToBody={true}
                    focused={openStartDate}
                    onFocusChange={() => toggleStartDropDowns('date')}
                    id="singleDatePicker"
                    numberOfMonths={1}
                    displayFormat="dd-MMM-yyyy"
                    isOutsideRange={(day: Moment) => day.isAfter(moment().endOf('day'))}
                    noBorder
                    disabled={isLoadingStartInfusion}
                  />
                  <div className="start-input-time-input">
                    <TextInput
                      padding={'8.91px'}
                      value={tempStartTime}
                      onChange={(e: any) => {
                        setTempStartTime(e.target.value.replace(/[^0-9:]/g, ''));
                        setStartDateTimeEdited(true);
                      }}
                      width={160.2}
                      borderColor={!validStartTime ? warning : ''}
                      borderWidth={!validStartTime ? '1.5px' : ''}
                      onClick={() => toggleStartDropDowns('time')}
                      disabled={isLoadingStartInfusion}
                    />
                    {openStartTime && (
                      <div className="start-input-time-dropdown">
                        <TimeDropdown
                          columnWidth={60}
                          isOpen={openStartTime}
                          onTimeSelect={({ hour, minute, second }: any) => {
                            setTempStartTime(`${hour}:${minute}:${second}`);
                            setStartDateTimeEdited(true);
                          }}
                          xTranslation={0} // Can be adjusted or removed
                          startTime={tempStartTime}
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="edit-container">
              <Button
                icon={isLoadingStartInfusion ? <LoadingSpinner width={25} height={25} /> : <Pencil fill={isInfusionStarted ? neutral400 : primaryBlue400} />}
                onClick={() => {
                  setStartInfusionEditing(!startInfusionEditing);
                  setTempStartTime(tempStartTime);
                }}
                width={'40px'}
                height={'40px'}
                disabled={isInfusionStarted || isLoadingStartInfusion}
                className={clsx('square-icon-button', startInfusionEditing && isLoadingStartInfusion && 'edit-button-enabled')}
              ></Button>
            </div>
            <Button
              label={isLoadingStartInfusion ? '' : 'Start Infusion'}
              onClick={handleStartInfusionClick}
              icon={<Clock fill={!startInfusionEditing ? neutral400 : neutral50} />}
              iconPlacement="left"
              disabled={isLoadingStartInfusion || isInfusionStarted || isInfusionStarted || !validStartTime}
              width={'157px'}
              height={'40px'}
              className={clsx('infusion-button', !isLoadingStartInfusion && !isInfusionStarted && 'infusion-button-enabled')}
            ></Button>
          </div>
          <div className="end-container">
            <div className={clsx(showAdd && 'show-add-button', 'end-inputs-container')}>
              <div className="start-infusion-spacing">
                {!editEndTime || isLoadingEndInfusion ? (
                  <>
                    <TextInput value={'Manually Log Date'} onChange={() => null} width={162} disabled={true} />
                    <TextInput value={'Log End Time'} onChange={() => null} width={162} disabled={true} />
                  </>
                ) : (
                  <div className="start-infusion-spacing-editing">
                    <SingleDatePicker
                      placeholder={convertDate(tempEndDate)}
                      date={displayEndDate()}
                      onDateChange={(inpputedDate: Moment | null) => {
                        if (inpputedDate) {
                          const combinedDateTime = inpputedDate
                            .clone()
                            .hour(Number(tempEndTime.split(':')[0]))
                            .minute(Number(tempEndTime.split(':')[1]))
                            .second(Number(tempEndTime.split(':')[2]))
                            .millisecond(0);
                          const formattedInput = combinedDateTime.toISOString();
                          setTempEndDate(formattedInput);
                          setEndDateTimeEdited(true);
                        }
                        setOpenEndDate(!openStartDate);
                      }}
                      appendToBody={true}
                      focused={openEndDate}
                      onFocusChange={() => toggleEndDropDowns('date')}
                      id="singleDatePicker"
                      numberOfMonths={1}
                      displayFormat="dd-MMM-yyyy"
                      isOutsideRange={(day: Moment) => day.isAfter(moment().endOf('day'))}
                      noBorder
                      disabled={isLoadingEndInfusion}
                    />
                    <div className="start-input-time-input">
                      <TextInput
                        padding={'8.91px'}
                        value={tempEndTime}
                        onChange={(e: any) => {
                          setTempEndTime(e.target.value.replace(/[^0-9:]/g, ''));
                          setEndDateTimeEdited(true);
                        }}
                        width={160.2}
                        borderColor={!validEndTime ? warning : ''}
                        borderWidth={!validEndTime ? '1.5px' : ''}
                        onClick={() => toggleEndDropDowns('time')}
                        disabled={isLoadingEndInfusion}
                      />
                      {openEndTime && (
                        <div className="start-input-time-dropdown">
                          <TimeDropdown
                            columnWidth={60}
                            isOpen={openEndTime}
                            onTimeSelect={({ hour, minute, second }: any) => {
                              setTempEndTime(`${hour}:${minute}:${second}`);
                              setEndDateTimeEdited(true);
                            }}
                            xTranslation={0} // Can be adjusted or removed
                            startTime={tempEndTime}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div className="edit-container">
                <Button
                  icon={isLoadingEndInfusion ? <LoadingSpinner width={25} height={25} /> : <Pencil fill={!isInfusionStarted ? neutral400 : primaryBlue400} />}
                  onClick={handleEditEndInfusionTimeClick}
                  width={'40px'}
                  height={'40px'}
                  disabled={!isInfusionStarted || isLoadingEndInfusion}
                  className={clsx('square-icon-button', isInfusionStarted && 'edit-button-enabled')}
                ></Button>
              </div>
              <Button
                label={isLoadingEndInfusion ? '' : 'End Infusion'}
                onClick={() => handleEndInfusionClick(undefined, undefined, undefined, undefined)}
                icon={<Clock fill={!editEndTime ? neutral400 : neutral50} />}
                iconPlacement="left"
                disabled={!isInfusionStarted || isLoadingEndInfusion || !validEndTime}
                width={'157px'}
                height={'40px'}
                className={clsx('infusion-button', isInfusionStarted && 'infusion-button-enabled')}
              ></Button>
            </div>
          </div>
        </div>
      </div>

      <div className="title">history</div>

      <div className={'history-table-container'}>
        <table className="table-container">
          <Header>
            <HeaderCell>
              <div>infusion status</div>
              <FilterButton onClick={() => {}} width={24} height={24} />
            </HeaderCell>
            <HeaderCell>
              <div>infusion nurse</div>
              <FilterButton onClick={() => {}} width={24} height={24} />
            </HeaderCell>
            <HeaderCell>
              <div className="time-format-cell">
                <div>infusion date</div>
                <div style={{ fontSize: '12px', color: '#888', marginTop: '4px', textAlign: 'center', textTransform: 'none' }}>dd:MMM:YYYY</div>
              </div>
              <FilterButton onClick={() => {}} width={24} height={24} />
            </HeaderCell>
            <HeaderCell>
              <div className="time-format-cell">
                <div>start time</div>
                <div style={{ fontSize: '12px', color: '#888', marginTop: '4px', textAlign: 'center', textTransform: 'none' }}>HH:mm:ss</div>
              </div>
              <FilterButton onClick={() => {}} width={24} height={24} />
            </HeaderCell>
            <HeaderCell>
              <div className="time-format-cell">
                <div>end time</div>
                <div style={{ fontSize: '12px', color: '#888', marginTop: '4px', textAlign: 'center', textTransform: 'none' }}>HH:mm:ss</div>
              </div>
              <FilterButton onClick={() => {}} width={24} height={24} />
            </HeaderCell>
            <HeaderCell>action</HeaderCell>
          </Header>
          <tbody className="body-container">
            {infusionSessions &&
              infusionSessions.length > 0 &&
              infusionSessions.map((session, index) => (
                <InfusionHistoryRow
                  session={session}
                  index={index}
                  key={session._id}
                  handleEndInfusionClick={(editedStartTime, editedEndTime, sessionId, sessionVersion) =>
                    handleEndInfusionClick(editedStartTime, editedEndTime, sessionId, sessionVersion)
                  }
                />
              ))}
          </tbody>
        </table>
      </div>
      <div className="loader-container">
        {isInfusionHistoryLoading && <LoadingSpinner width={mediumSpinner} height={mediumSpinner} />}
        {serverError && <p>Error:</p>}
      </div>
    </div>
  ) : (
    <></>
  );

  return (
    <div className="patient-infusion-container" data-testid="patient-infusion-card">
      <Card title={'INFUSION ACTIVITIES'} body={body} expandable expanded={expanded} onExpand={handleOnExpand} />
    </div>
  );
};
